/**
 * Collaboration Panel Component
 * 
 * Displays active collaborators, invite link generation, and connection status
 * Miro-style compact UI for real-time collaboration
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getProjectCollaborators, isProjectConnected, subscribeProjectAwareness } from '../utils/collaborationAwarenessRegistry';
import { generateInviteToken } from '../services/inviteService';
import CompactCollaborationBar from './CompactCollaborationBar';
import './CollaborationPanel.css';

const CollaborationPanel = ({ projectId, userId, username }) => {
  const [collaborators, setCollaborators] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [inviteLink, setInviteLink] = useState(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [isGeneratingInvite, setIsGeneratingInvite] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Collaborators here reflect whoever's editor has an active Yjs connection
  // for a file in this project right now (see collaborationAwarenessRegistry.js's
  // header comment on why this is file-level, not truly project-wide).
  useEffect(() => {
    const refresh = () => {
      setCollaborators(getProjectCollaborators(projectId));
      setConnectionStatus(isProjectConnected(projectId) ? 'connected' : 'disconnected');
    };
    refresh();
    return subscribeProjectAwareness(projectId, refresh);
  }, [projectId]);

  const handleGenerateInvite = async () => {
    setIsGeneratingInvite(true);
    try {
      const result = await generateInviteToken(projectId, userId, 24);
      setInviteLink(result.inviteLink);
      setShowInviteDialog(true);
    } catch (error) {
      console.error('Failed to generate invite:', error);
      alert('Failed to generate invite link. Please try again.');
    } finally {
      setIsGeneratingInvite(false);
    }
  };

  const handleCopyInviteLink = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink).then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      }).catch((error) => {
        console.error('Failed to copy invite link:', error);
        alert('Failed to copy invite link');
      });
    }
  };

  const getStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected':
        return '🟢';
      case 'disconnected':
        return '🔴';
      case 'online':
        return '🟢';
      case 'offline':
        return '🔴';
      case 'failed':
        return '⚠️';
      default:
        return '⚪';
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Connected';
      case 'disconnected':
        return 'Disconnected';
      case 'online':
        return 'Online';
      case 'offline':
        return 'Offline';
      case 'failed':
        return 'Connection Failed';
      case 'unavailable':
        return 'Backend Not Ready';
      default:
        return 'Unknown';
    }
  }

  // Deduplicate collaborators by userId and exclude current user
  const uniqueCollaborators = collaborators.reduce((acc, collab) => {
    // Skip if this is the current user
    if (collab.userId === userId) {
      return acc;
    }
    // Check if we already have this userId
    const existing = acc.find(c => c.userId === collab.userId);
    if (!existing) {
      acc.push({
        ...collab,
        isOnline: true, // All fetched collaborators are online
      });
    }
    return acc;
  }, []);

  // Current user info
  const currentUser = {
    userId,
    username,
    email: userId,
    color: '#6366f1',
  };

  return (
    <div className="collaboration-panel">
      <CompactCollaborationBar
        connectionStatus={connectionStatus}
        collaborators={uniqueCollaborators}
        currentUser={currentUser}
        onInvite={handleGenerateInvite}
      />

      {/* Invite Dialog */}
      {showInviteDialog && (
        <div className="invite-dialog-overlay" onClick={() => setShowInviteDialog(false)}>
          <div className="invite-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="invite-dialog-header">
              <h3>🔗 Share Invite Link</h3>
              <button
                className="invite-dialog-close"
                onClick={() => setShowInviteDialog(false)}
              >
                ✕
              </button>
            </div>
            <div className="invite-dialog-body">
              <p>Share this link with collaborators to give them access to this project:</p>
              <div className="invite-link-container">
                <input
                  type="text"
                  className="invite-link-input"
                  value={inviteLink || ''}
                  readOnly
                />
                <button
                  className="copy-button"
                  onClick={handleCopyInviteLink}
                >
                  {copySuccess ? '✓ Copied' : '📋 Copy'}
                </button>
              </div>
              <p className="invite-expiry">⏰ Link expires in 24 hours</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

CollaborationPanel.propTypes = {
  projectId: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  username: PropTypes.string.isRequired,
};

export default CollaborationPanel;
